import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import {
  X,
  User,
  Lock,
  Mail,
  Eye,
  EyeOff,
  Save,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Shield,
  Settings,
} from "lucide-react";

function ModalConfiguracionCuenta({ isOpen, onClose, usuario, constructora, onUsuarioUpdate }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [alert, setAlert] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Estados del formulario
  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    email: "",
    password_actual: "",
    password_nueva: "",
    password_confirmar: "",
  });

  // Cargar datos del usuario al abrir el modal
  useEffect(() => {
    if (isOpen && usuario) {
      setFormData({
        nombre: usuario.nombre || "",
        apellido: usuario.apellido || "",
        email: usuario.email || "",
        password_actual: "",
        password_nueva: "",
        password_confirmar: "",
      });
    }
  }, [isOpen, usuario]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Cerrar modal con ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  const showAlert = (type, title, message) => {
    setAlert({ type, title, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validatePassword = (password) => {
    if (password.length < 6) {
      return "La contraseña debe tener al menos 6 caracteres";
    }
    return null;
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setLoading(true);

    try {
      // Validar que los campos básicos no estén vacíos
      if (!formData.nombre.trim() || !formData.apellido.trim()) {
        showAlert("error", "Datos Incompletos", "El nombre y apellido son obligatorios");
        return;
      }

      // Actualizar datos del perfil en usuarios
      const { error } = await supabase
        .from("usuarios")
        .update({
          nombre: formData.nombre.trim(),
          apellido: formData.apellido.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", usuario.id);

      if (error) throw error;

      // Intentar actualizar también en trabajadores (si existe)
      try {
        const { data: trabajador, error: trabError } = await supabase
          .from('trabajadores')
          .select('id, nombre, apellido')
          .eq('email', usuario.email)
          .eq('constructora_id', usuario.constructora_id)
          .eq('activo', true);

        if (!trabError && trabajador && trabajador.length > 0) {
          const trabajadorEncontrado = trabajador[0];
          
          const { error: updateError } = await supabase
            .from('trabajadores')
            .update({
              nombre: formData.nombre.trim(),
              apellido: formData.apellido.trim(),
            })
            .eq('id', trabajadorEncontrado.id);

          if (updateError) {
            console.error('❌ Error actualizando trabajador:', updateError);
          } else {
            console.log('✅ Trabajador actualizado exitosamente');
          }
        }
      } catch (trabError) {
        console.log('⚠️ Error en proceso de actualización de trabajador:', trabError.message);
        // No fallar la operación completa si no se puede actualizar el trabajador
      }

      showAlert("success", "Perfil Actualizado", "Los datos de tu perfil se han actualizado correctamente");
      
      // Actualizar el estado del usuario en el componente padre
      if (onUsuarioUpdate) {
        onUsuarioUpdate({
          ...usuario,
          nombre: formData.nombre.trim(),
          apellido: formData.apellido.trim(),
        });
      }
      
      // Limpiar campos de contraseña
      setFormData(prev => ({
        ...prev,
        password_actual: "",
        password_nueva: "",
        password_confirmar: "",
      }));

    } catch (err) {
      console.error("Error actualizando perfil:", err);
      showAlert("error", "Error al Actualizar", "Error: " + err.message);
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setSaving(true);
    setLoading(true);

    try {
      // Validaciones
      if (!formData.password_actual) {
        showAlert("error", "Contraseña Actual Requerida", "Debes ingresar tu contraseña actual");
        return;
      }

      const passwordError = validatePassword(formData.password_nueva);
      if (passwordError) {
        showAlert("error", "Contraseña Inválida", passwordError);
        return;
      }

      if (formData.password_nueva !== formData.password_confirmar) {
        showAlert("error", "Contraseñas No Coinciden", "La nueva contraseña y su confirmación no coinciden");
        return;
      }

      // Usar el endpoint del backend para cambio seguro de contraseña
      const response = await fetch("https://nokk-backend.vercel.app/api/cambiar-password-seguro", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: usuario.id,
          passwordActual: formData.password_actual,
          passwordNueva: formData.password_nueva,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error);
      }

      showAlert("success", "Contraseña Actualizada", "Tu contraseña ha sido cambiada exitosamente");
      
      // Limpiar campos de contraseña
      setFormData(prev => ({
        ...prev,
        password_actual: "",
        password_nueva: "",
        password_confirmar: "",
      }));

    } catch (err) {
      console.error("Error cambiando contraseña:", err);
      showAlert("error", "Error al Cambiar Contraseña", "Error: " + err.message);
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  const renderAlert = () => {
    if (!alert) return null;

    const alertStyles = {
      error: {
        background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
        border: "1px solid #fca5a5",
        color: "#dc2626"
      },
      success: {
        background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
        border: "1px solid #86efac",
        color: "#16a34a"
      },
      warning: {
        background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
        border: "1px solid #fcd34d",
        color: "#d97706"
      }
    };

    const Icon = alert.type === "error" ? AlertTriangle : CheckCircle;

    return (
      <div
        style={{
          ...alertStyles[alert.type],
          padding: "12px 16px",
          borderRadius: "10px",
          marginBottom: "20px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          fontSize: "14px",
          fontWeight: "500",
        }}
      >
        <Icon size={16} />
        <div>
          <div style={{ fontWeight: "600", marginBottom: "2px" }}>{alert.title}</div>
          <div>{alert.message}</div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: isMobile ? "16px" : "20px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "600px",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px 24px 0 24px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                backgroundColor: "#3b82f6",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Settings size={20} color="white" />
            </div>
            <div>
              <h2
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#1e293b",
                  margin: 0,
                }}
              >
                Configuración de Cuenta
              </h2>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: 0,
                }}
              >
                Gestiona tu información personal
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              padding: "8px",
              borderRadius: "8px",
              cursor: "pointer",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = "#f1f5f9";
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = "transparent";
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: "0 24px 24px 24px" }}>
          {/* Alert */}
          {renderAlert()}

          {/* Información de la constructora */}
          {constructora && (
            <div
              style={{
                background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                padding: "16px",
                borderRadius: "12px",
                marginBottom: "24px",
                border: "1px solid #93c5fd",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                <Settings size={16} color="#1d4ed8" />
                <span style={{ fontWeight: "600", color: "#1d4ed8", fontSize: "14px" }}>
                  {constructora.nombre_constructora}
                </span>
              </div>
              <div style={{ fontSize: "13px", color: "#1e40af" }}>
                Tipo de usuario: <strong>{usuario?.tipo_usuario}</strong>
              </div>
            </div>
          )}

          {/* Formulario de Perfil */}
          <div style={{ marginBottom: "32px" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#374151",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <User size={16} />
              Información Personal
            </h3>

            <div style={{ display: "grid", gap: "16px" }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "16px" }}>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Nombre *
                  </label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => handleInputChange("nombre", e.target.value)}
                    placeholder="Tu nombre"
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "10px",
                      fontSize: "14px",
                      outline: "none",
                      transition: "all 0.2s ease",
                      backgroundColor: "#fafafa",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#3b82f6";
                      e.target.style.backgroundColor = "white";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.backgroundColor = "#fafafa";
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Apellido *
                  </label>
                  <input
                    type="text"
                    value={formData.apellido}
                    onChange={(e) => handleInputChange("apellido", e.target.value)}
                    placeholder="Tu apellido"
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "10px",
                      fontSize: "14px",
                      outline: "none",
                      transition: "all 0.2s ease",
                      backgroundColor: "#fafafa",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#3b82f6";
                      e.target.style.backgroundColor = "white";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.backgroundColor = "#fafafa";
                    }}
                  />
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Email
                </label>
                <div style={{ position: "relative" }}>
                  <Mail size={18} color="#64748b" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)" }} />
                  <input
                    type="email"
                    value={formData.email}
                    disabled
                    style={{
                      width: "100%",
                      padding: "12px 12px 12px 44px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "10px",
                      fontSize: "14px",
                      outline: "none",
                      backgroundColor: "#f3f4f6",
                      color: "#6b7280",
                      cursor: "not-allowed",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <p style={{ fontSize: "12px", color: "#6b7280", margin: "6px 0 0 0" }}>
                  El email no se puede modificar por seguridad
                </p>
              </div>

              <button
                onClick={handleSaveProfile}
                disabled={loading || saving}
                style={{
                  padding: "12px 24px",
                  backgroundColor: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: loading || saving ? "not-allowed" : "pointer",
                  opacity: loading || saving ? 0.7 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.2s ease",
                }}
                onMouseOver={(e) => {
                  if (!loading && !saving) {
                    e.target.style.backgroundColor = "#2563eb";
                  }
                }}
                onMouseOut={(e) => {
                  if (!loading && !saving) {
                    e.target.style.backgroundColor = "#3b82f6";
                  }
                }}
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {saving ? "Guardando..." : "Guardar Perfil"}
              </button>
            </div>
          </div>

          {/* Formulario de Cambio de Contraseña */}
          <div>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#374151",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Lock size={16} />
              Cambiar Contraseña
            </h3>

            <div style={{ display: "grid", gap: "16px" }}>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Contraseña Actual *
                </label>
                <div style={{ position: "relative" }}>
                  <Lock size={18} color="#64748b" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)" }} />
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={formData.password_actual}
                    onChange={(e) => handleInputChange("password_actual", e.target.value)}
                    placeholder="Ingresa tu contraseña actual"
                    style={{
                      width: "100%",
                      padding: "12px 12px 12px 44px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "10px",
                      fontSize: "14px",
                      outline: "none",
                      transition: "all 0.2s ease",
                      backgroundColor: "#fafafa",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#3b82f6";
                      e.target.style.backgroundColor = "white";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.backgroundColor = "#fafafa";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#64748b",
                      padding: "4px",
                    }}
                  >
                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Nueva Contraseña *
                </label>
                <div style={{ position: "relative" }}>
                  <Lock size={18} color="#64748b" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)" }} />
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={formData.password_nueva}
                    onChange={(e) => handleInputChange("password_nueva", e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    style={{
                      width: "100%",
                      padding: "12px 12px 12px 44px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "10px",
                      fontSize: "14px",
                      outline: "none",
                      transition: "all 0.2s ease",
                      backgroundColor: "#fafafa",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#3b82f6";
                      e.target.style.backgroundColor = "white";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.backgroundColor = "#fafafa";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#64748b",
                      padding: "4px",
                    }}
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Confirmar Nueva Contraseña *
                </label>
                <div style={{ position: "relative" }}>
                  <Lock size={18} color="#64748b" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)" }} />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.password_confirmar}
                    onChange={(e) => handleInputChange("password_confirmar", e.target.value)}
                    placeholder="Repite la nueva contraseña"
                    style={{
                      width: "100%",
                      padding: "12px 12px 12px 44px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "10px",
                      fontSize: "14px",
                      outline: "none",
                      transition: "all 0.2s ease",
                      backgroundColor: "#fafafa",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#3b82f6";
                      e.target.style.backgroundColor = "white";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.backgroundColor = "#fafafa";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#64748b",
                      padding: "4px",
                    }}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                onClick={handleChangePassword}
                disabled={loading || saving || !formData.password_actual || !formData.password_nueva || !formData.password_confirmar}
                style={{
                  padding: "12px 24px",
                  backgroundColor: "#059669",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: (loading || saving || !formData.password_actual || !formData.password_nueva || !formData.password_confirmar) ? "not-allowed" : "pointer",
                  opacity: (loading || saving || !formData.password_actual || !formData.password_nueva || !formData.password_confirmar) ? 0.7 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.2s ease",
                }}
                onMouseOver={(e) => {
                  if (!loading && !saving && formData.password_actual && formData.password_nueva && formData.password_confirmar) {
                    e.target.style.backgroundColor = "#047857";
                  }
                }}
                onMouseOut={(e) => {
                  if (!loading && !saving && formData.password_actual && formData.password_nueva && formData.password_confirmar) {
                    e.target.style.backgroundColor = "#059669";
                  }
                }}
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} />}
                {saving ? "Cambiando..." : "Cambiar Contraseña"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModalConfiguracionCuenta;
